import { action } from "typesafe-actions";
import { IBooking } from "./booking.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../api/api";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { IRoughEstimate } from "../leads/leads.types";
import { ISearchQueryParams } from "../store.types";
import { getSearchQueryFromParams } from "../store.utils";
import axios, { CancelTokenSource } from "axios";
import moment from "moment";

const getMonthStartEndDate = (month: string, year: string) => {
  const formattedMonth = month.padStart(2, "0");

  const start = moment(`${year}-${formattedMonth}-01`)
    .startOf("month")
    .format("YYYY-MM-DD");
  const end = moment(`${year}-${formattedMonth}-01`)
    .endOf("month")
    .format("YYYY-MM-DD");

  return { start, end };
};

const getFullYearStartEndDate = (year: string) => {
  const start = moment(`${year}-01-01`).format("YYYY-MM-DD");
  const end = moment(`${year}-12-31`).format("YYYY-MM-DD");

  return { start, end };
};

export const FETCH_BOOKING_LIST_PROGRESS = "FETCH_BOOKING_LIST_PROGRESS";
export const FETCH_BOOKING_LIST_SUCCESS = "FETCH_BOOKING_LIST_SUCCESS";
export const FETCH_BOOKING_LIST_FAILED = "FETCH_BOOKING_LIST_FAILED";

export const fetchBookingListProgress = () =>
  action(FETCH_BOOKING_LIST_PROGRESS);
export const fetchBookingListSuccess = (
  data: IBooking[],
  totalRecords: number,
) =>
  action(FETCH_BOOKING_LIST_SUCCESS, {
    data,
    totalRecords,
  });
export const fetchBookingListFailed = () => action(FETCH_BOOKING_LIST_FAILED);

export const fetchBookingByCustomerAsync =
  (
    customer_fact_uuid: string,
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    date: IDataTableV2DateState["dates"],
    company_outlet_uuid?: string,
    searchValue?: IDatatableV2AdvancedSearchFilter,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchBookingListProgress());

      let url = `/occupancy/get-booking?customer_fact_uuid=${customer_fact_uuid}&pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}&upcoming_bookings=false`;
      if (searchValue && searchValue.length > 0) {
        const advanceFilter = [...searchValue];

        if (date?.fromDate && date?.toDate) {
          advanceFilter.push({
            column: ["from_date"],
            value: date.fromDate,
            logicalOperator: "AND",
            operator: "GREATER_THAN_EQUAL",
          });
          advanceFilter.push({
            column: ["from_date"],
            value: date.toDate,
            logicalOperator: "AND",
            operator: "LESSER_THAN_EQUAL",
          });
        }

        url += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
      }

      if (company_outlet_uuid) {
        url += `&company_outlet_uuid=${company_outlet_uuid}`;
      }
      const res = await api.get(url);
      const data: IBooking[] = res.data.data;
      const totalRecords = res.data.totalRecords;
      dispatch(fetchBookingListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to fetch bookings",
          displayAs: "snackbar",
        }),
      );
    }
  };

let cancelToken: CancelTokenSource | null = null;

/**
 * Fetch upcoming bookings with unified ISearchQueryParams
 */
export const fetchBookingListAsync =
  (params: ISearchQueryParams): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      if (cancelToken) {
        cancelToken.cancel("Operation canceled due to a new request.");
      }

      cancelToken = axios.CancelToken.source();

      dispatch(fetchBookingListProgress());
      dispatch(saveLoaderProgress());

      // Build query and append upcoming_booking flag
      const query = getSearchQueryFromParams(params);
      const separator = query ? "&" : "?";
      const url = `/occupancy/get-booking${query}${separator}upcoming_booking=true`;

      const res = await api.get(url, { cancelToken: cancelToken.token });

      const data: IBooking[] = res.data.data;
      const totalRecords = res.data.totalRecords;
      dispatch(fetchBookingListSuccess(data, totalRecords));
    } catch (err: any) {
      if (axios.isCancel(err)) {
        console.log("Previous request canceled", err.message);
      } else {
        dispatch(fetchBookingListFailed());
        dispatch(
          showMessage({
            type: "error",
            message: err.response?.data?.message || "An error occurred",
            displayAs: "snackbar",
          }),
        );
      }
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

let cancelTokenTotalBooking: CancelTokenSource | null = null;

/**
 * Fetch all bookings (not just upcoming) with unified ISearchQueryParams
 */
export const fetchTotalBookingListAsync =
  (params: ISearchQueryParams): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      if (cancelTokenTotalBooking) {
        cancelTokenTotalBooking.cancel(
          "Operation canceled due to a new request.",
        );
      }

      cancelTokenTotalBooking = axios.CancelToken.source();

      dispatch(fetchBookingListProgress());
      dispatch(saveLoaderProgress());

      // Build query string directly from params
      const query = getSearchQueryFromParams(params);

      const url = `/occupancy/get-booking${query}`;

      const res = await api.get(url, {
        cancelToken: cancelTokenTotalBooking.token,
      });

      const data: IBooking[] = res.data.data;
      const totalRecords = res.data.totalRecords;
      dispatch(fetchBookingListSuccess(data, totalRecords));
    } catch (err: any) {
      if (axios.isCancel(err)) {
        console.log("Previous request canceled", err.message);
      } else {
        dispatch(fetchBookingListFailed());
        dispatch(
          showMessage({
            type: "error",
            message: err.response?.data?.message || "An error occurred",
            displayAs: "snackbar",
          }),
        );
      }
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_INVOICE_LIST_PROGRESS = "FETCH_INVOICE_LIST_PROGRESS";
export const FETCH_INVOICE_LIST_SUCCESS = "FETCH_INVOICE_LIST_SUCCESS";
export const FETCH_INVOICE_LIST_FAILED = "FETCH_INVOICE_LIST_FAILED";

export const fetchInvoiceListProgress = () =>
  action(FETCH_INVOICE_LIST_PROGRESS);
export const fetchInvoiceListSuccess = (
  data: IRoughEstimate[],
  totalRecords: number,
) =>
  action(FETCH_INVOICE_LIST_SUCCESS, {
    data,
    totalRecords,
  });
export const fetchInvoiceListFailed = () => action(FETCH_INVOICE_LIST_FAILED);

export const fetchInvoiceByCustomerAsync =
  (
    contact_number: string,
    pageNumber: number,
    rowsInPerPage: number,
    date: IDataTableV2DateState["dates"],
    company_outlet_uuid?: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchInvoiceListProgress());

      let url = `/customer/get-all-past-bills?contact_number=${contact_number}&pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;

      if (company_outlet_uuid) {
        url += `&company_outlet_uuid=${company_outlet_uuid}`;
      }
      const res = await api.get(url);
      const data: IRoughEstimate[] = res.data.data;
      const totalRecords = res.data.totalRecords;
      dispatch(fetchInvoiceListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to fetch invoices",
          displayAs: "snackbar",
        }),
      );
    }
  };

export const fetchInvoicePreviewAsync =
  (
    rough_estimate_uuids: string[],
    type: string,

    onCallback: (isSuccess: boolean, html?: any) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const payload = {
        rough_estimate_uuids,
        type: "PREVIEW",
      };

      const res = await api.post(`/customer/generate-bill`, payload);

      onCallback(true, res.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
      onCallback(false);
    }
  };

export const fetchInvoiceDownloadAsync =
  (
    rough_estimate_uuids: string[],
    type: string,
    onCallback: (isSuccess: boolean) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const payload = {
        rough_estimate_uuids,
        type: "PDF",
      };

      const res = await api.post(`/customer/generate-bill`, payload, {
        responseType: "arraybuffer",
        validateStatus: (status) => status === 200,
      });

      const contentType = res.headers["content-type"];
      if (!contentType.includes("application/pdf")) {
        throw new Error(`Invalid content type: ${contentType}`);
      }

      dispatch(saveLoaderCompleted());

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "invoice.pdf";
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);

      onCallback(true);
    } catch (err: any) {
      dispatch(saveLoaderCompleted());

      let errorMessage = "Failed to download PDF";
      if (err.response?.data) {
        const decoder = new TextDecoder("utf-8");
        const errorData = decoder.decode(err.response.data);
        try {
          errorMessage = JSON.parse(errorData).message || errorMessage;
        } catch {
          errorMessage = errorData;
        }
      }

      dispatch(
        showMessage({
          type: "error",
          message: errorMessage,
          displayAs: "snackbar",
        }),
      );
      onCallback(false);
    }
  };

export const CLEAR_BOOKING_STATE = "CLEAR_BOOKING_STATE";

export const clearBookingState = () => action(CLEAR_BOOKING_STATE);
